import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  fileAccepted,
  fileMatchSize,
  whiteListTypes,
  resizeImage,
  detectFileType,
  generatePreview,
  renameFileToJpeg,
  validateFilename,
  MAX_FILE_SIZE,
  MAX_HEIC_SIZE,
} from "../utils";
import { convertHeic } from "../../../../actions/claimActionsPP";

export const useFileProcessing = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  const processFiles = (fileList, options) => {
    const { disablePreview, maxSize, minSize } = options;
    const acceptedFiles = [];
    const rejectedFiles = [];

    const promises = fileList.map(async file => {
      if (!validateFilename(file.name)) {
        alert("Receipt file name contains invalid characters. Please remove special characters and try again.");
        return;
      }
      const processedFile = await processFileByType(file);
      generatePreview(processedFile, disablePreview);

      if (
        fileAccepted(processedFile, "application/pdf") ||
        fileAccepted(processedFile, whiteListTypes)
      ) {
        if (fileMatchSize(processedFile, maxSize, minSize)) {
          acceptedFiles.push(processedFile);
        } else {
          rejectedFiles.push(processedFile);
        }
      } else {
        rejectedFiles.push(processedFile);
      }
    });

    return Promise.all(promises).then(() => ({ acceptedFiles, rejectedFiles }));
  };

  const processFileByType = useCallback(
    async file => {
      setIsProcessing(true);
      setError(null);

      try {
        const fileType = await detectFileType(file);

        switch (fileType) {
          case "jfif":
            return await processJfifFile(file);
          case "heic":
          case "heif":
            return await processHeicFile(file);
          case "image/jpeg":
          case "image/jpg":
          case "image/png":
            return await processImageFile(file);
          default:
            return file;
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsProcessing(false);
      }
    },
    [processJfifFile, processHeicFile, processImageFile]
  );

  const processJfifFile = useCallback(async file => {
    try {
      const jpegFile = new File([file], file.name, {
        type: "image/jpeg",
      });
      const resizedFile = await resizeImage(jpegFile, MAX_FILE_SIZE);
      const renamedFile = new File([resizedFile], renameFileToJpeg(resizedFile.name), {
        type: "image/jpeg",
      });
      return renamedFile;
    } catch (error) {
      console.error("Error processing JFIF file: ", error);
      throw new Error(`Error processing JFIF file: ${error.message}`);
    }
  }, []);

  const processHeicFile = useCallback(
    async file => {
      try {
        if (file.size > MAX_HEIC_SIZE) {
          throw new Error(
            `HEIC file size exceeds the maximum limit of 10MB. Current size: ${(
              file.size /
              (1024 * 1024)
            ).toFixed(2)}MB`
          );
        }
        const heicToJpegFile = await convertHeicToJpeg(file);
        return await resizeImage(heicToJpegFile, MAX_FILE_SIZE);
      } catch (error) {
        console.error("Error processing HEIC file: ", error);
        throw new Error(`Error processing HEIC file: ${error.message}`);
      }
    },
    [convertHeicToJpeg]
  );

  const processImageFile = useCallback(async file => {
    try {
      const resizedFile = await resizeImage(file, MAX_FILE_SIZE);
      const renamedFile = new File([resizedFile], renameFileToJpeg(resizedFile.name), {
        type: "image/jpeg",
      });
      return renamedFile;
    } catch (error) {
      console.error("Error processing image file: ", error);
      throw new Error(`Error processing image file: ${error.message}`);
    }
  }, []);

  const convertHeicToJpeg = useCallback(
    async file => {
      try {
        const blob = await dispatch(convertHeic(file));
        return new File([blob], renameFileToJpeg(file.name), { type: "image/jpeg" });
      } catch (error) {
        console.error("Error converting HEIC to JPEG:", error);
        throw error;
      }
    },
    [dispatch]
  );

  return { processFiles, isProcessing, error };
};



// WEBPACK FOOTER //
// ./components/common/file-uploader/hooks/useFileProcessing.js